import { ApplicationController, observe, unobserve } from '@aha-app/mvc';
import WidgetsController from '../../../../controllers/WidgetsController';
import TimerController from './TimerController';
import type TimerWidget from '../../../../widgets/TimerWidget';
import { TimerStatus } from '../../../../widgets/TimerWidget';
import trackEvent from '../../../../utils/analytics';
import {
  DEFAULT_DURATION,
  EXACT_MAX_TIME,
  MINUTE,
  timeInMillis,
  elapsedTime,
  formattedTime,
} from '../../../../utils/time';

interface State {
  widget: TimerWidget;
  remainingDurationText: string;
}

interface ControllerProps {
  widget: TimerWidget;
}

class TimerCountdownController extends ApplicationController<
  State,
  ControllerProps
> {
  widgetsController: WidgetsController;
  timerController: TimerController;
  widgetObserver: ReturnType<typeof observe>;
  widgetListener: () => void;

  static initialState: State = {
    widget: null as any,
    remainingDurationText: formattedTime(DEFAULT_DURATION),
  };

  async initialize({ widget }): Promise<void> {
    this.widgetsController = this.findControllerInstance(WidgetsController)!;
    this.timerController = this.findControllerInstance(TimerController)!;
    this.state.widget = widget;

    this.widgetListener = () => this.actionStopTimer(true);

    this.widgetObserver = observe(() => this.actionUpdateRemainingDuration());

    document.addEventListener(
      'aha.whiteboard.timerStoppedWidget',
      this.widgetListener
    );
  }

  destroy(): void {
    unobserve(this.widgetObserver);

    document.removeEventListener(
      'aha.whiteboard.timerStoppedWidget',
      this.widgetListener
    );
  }

  actionUpdateRemainingDuration() {
    this.state.remainingDurationText = formattedTime(
      this.state.widget.remainingDuration()
    );
  }

  actionAddTime(time: number): void {
    if (!this.state.widget.active()) {
      return;
    }

    if (this.state.widget.expired()) {
      // Soft restart
      const startedAt = Date.now();
      const exactDuration = timeInMillis(time);

      let status;

      if (this.state.widget.paused()) {
        status = TimerStatus.Paused;
      } else {
        status = TimerStatus.Active;
      }

      this.widgetsController.actionUpdateWidget(this.state.widget, {
        startedAt,
        exactDuration,
        status,
      });
    } else {
      const elapsed = elapsedTime(this.state.widget.startedAt!);
      const newTimeRemaining =
        this.state.widget.exactDuration - elapsed + timeInMillis(time);

      if (newTimeRemaining > EXACT_MAX_TIME) {
        this.widgetsController.actionUpdateWidget(this.state.widget, {
          exactDuration: EXACT_MAX_TIME + elapsed,
        });
      } else {
        this.widgetsController.actionUpdateWidget(this.state.widget, {
          exactDuration: this.state.widget.exactDuration + timeInMillis(time),
        });
      }
    }

    trackEvent('Feature-7236748624623262225', 'User extends whiteboard timer', {
      'Extra Minutes': time === MINUTE ? 1 : 5,
    });

    this.actionUpdateRemainingDuration();
  }

  // Signal to the widget to stop showing the 'expired' UX
  notifyWidgetComponent(): void {
    document.dispatchEvent(
      new CustomEvent('aha.whiteboard.timerStoppedSidePanel')
    );
  }

  actionStopTimer(fromWidget: boolean = false): void {
    const timerController = this.findControllerInstance(TimerController)!;
    if (!this.timerController.state.active) return;

    if (!this.state.widget.active()) {
      if (!fromWidget) this.notifyWidgetComponent();
      timerController.actionSetActive(false);

      return;
    }

    const data = {
      status: TimerStatus.Inactive,
      startedAt: null,
      exactDuration: timeInMillis(this.state.widget.startDuration),
    };

    this.widgetsController.actionUpdateWidget(this.state.widget, data);

    if (!fromWidget) {
      this.notifyWidgetComponent();
      trackEvent('Feature-7236748624623262225', 'User ends whiteboard timer');
    }

    timerController.actionSetActive(false);
  }

  actionPauseTimer(): void {
    if (!this.state.widget.active() || this.state.widget.expired()) return;

    const data = {
      status: TimerStatus.Paused,
      exactDuration:
        this.state.widget.exactDuration -
        elapsedTime(this.state.widget.startedAt!),
    };

    this.widgetsController.actionUpdateWidget(this.state.widget, data);
    this.actionUpdateRemainingDuration();

    trackEvent('Feature-7236748624623262225', 'User pauses whiteboard timer');
  }

  actionUnpauseTimer(): void {
    if (this.state.widget.expired() || !this.state.widget.paused()) return;

    const data = {
      status: TimerStatus.Active,
      startedAt: Date.now(),
    };

    this.widgetsController.actionUpdateWidget(this.state.widget, data);

    trackEvent('Feature-7236748624623262225', 'User unpauses whiteboard timer');
  }
}

export default TimerCountdownController;
